import { cn } from "@/lib/utils";
import { Slot } from "@rn-primitives/slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { Platform, Text as RNText, type Role } from "react-native";

const textVariants = cva(
    cn(
        "text-foreground text-base",
        Platform.select({
            web: "select-text",
        }),
    ),
    {
        variants: {
            variant: {
                default: "font-sans text-base",
                h1: cn(
                    "font-display-bold text-2xl tracking-tight",
                    Platform.select({ web: "scroll-m-20 text-balance" }),
                ),
                h2: cn(
                    "font-display text-xl tracking-tight",
                    Platform.select({ web: "scroll-m-20" }),
                ),
                h3: cn(
                    "font-display text-lg tracking-tight",
                    Platform.select({ web: "scroll-m-20" }),
                ),
                h4: cn("font-sans-medium text-base tracking-tight"),
                p: "font-sans text-base leading-6",
                blockquote: "mt-4 border-l-2 border-border pl-3 italic",
                code: cn(
                    "bg-muted relative rounded px-[0.3rem] py-[0.2rem] font-mono text-sm font-semibold",
                ),
                lead: "font-sans text-lg text-muted-foreground",
                large: "font-sans-medium text-lg",
                bold: "font-sans-medium",
                // Avoid leading-none: on native it clips diacritics (e.g. Č) above the first line.
                small: "font-sans-medium text-sm",
                muted: "font-sans text-sm text-muted-foreground",
                label: "font-sans-medium text-sm",
                caption: "font-sans text-sm text-muted-foreground",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    },
);

type TextVariantProps = VariantProps<typeof textVariants>;

type TextVariant = NonNullable<TextVariantProps["variant"]>;

const ROLE: Partial<Record<TextVariant, Role>> = {
    h1: "heading",
    h2: "heading",
    h3: "heading",
    h4: "heading",
    blockquote: Platform.select({ web: "blockquote" as Role }),
    code: Platform.select({ web: "code" as Role }),
};

const ARIA_LEVEL: Partial<Record<TextVariant, string>> = {
    h1: "1",
    h2: "2",
    h3: "3",
    h4: "4",
};

const TextClassContext = React.createContext<string | undefined>(undefined);

function Text({
    className,
    asChild = false,
    variant = "default",
    ...props
}: React.ComponentProps<typeof RNText> &
    React.RefAttributes<typeof RNText> &
    TextVariantProps & {
        asChild?: boolean;
    }) {
    const textClass = React.useContext(TextClassContext);
    const Component = asChild ? Slot : RNText;
    return (
        <Component
            className={cn(textVariants({ variant }), textClass, className)}
            role={variant ? ROLE[variant] : undefined}
            aria-level={variant ? ARIA_LEVEL[variant] : undefined}
            {...props}
        />
    );
}

export { Text, TextClassContext };
